import { StageType } from "../../../graphql/types";
import type {
  RoiCalculatorFormData,
  RoiFarmGateInputs,
} from "../models/RoiCalculator";
import {
  FARM_GATE_FIELD_MAP,
  normalizeStageType,
  ProcessingOutputItem,
  RoiFormData,
} from "./roiStageData";

export interface RoiFormInputs {
  currency: string;
  feedCostPerTonne: {
    baseline: Partial<Record<StageType, number>>;
    intervention: Partial<Record<StageType, number>>;
  };
  inclusionCostOfSolution?: number;
  farmGate: {
    baseline: Record<string, number>;
    intervention: Record<string, number>;
  };
  carbonPricePerTonne?: number;
}

const toFiniteNumber = (raw: unknown): number | undefined => {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : undefined;
  if (typeof raw !== "string") return undefined;
  if (raw.trim() === "") return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
};

const buildFeedCostPerTonne = (
  stages: RoiCalculatorFormData["stages"],
): RoiFormInputs["feedCostPerTonne"] => {
  const baseline: Partial<Record<StageType, number>> = {};
  const intervention: Partial<Record<StageType, number>> = {};

  stages.forEach((stage) => {
    if (!stage.stageType) return;
    const key = normalizeStageType(stage.stageType) as StageType;
    const baselineValue = toFiniteNumber(stage.baselineInputs?.feedCost);
    const interventionValue = toFiniteNumber(
      stage.interventionInputs?.feedCost,
    );
    if (baselineValue !== undefined) baseline[key] = baselineValue;
    if (interventionValue !== undefined) intervention[key] = interventionValue;
  });

  return { baseline, intervention };
};

const buildFarmGateSide = (
  inputs: RoiFarmGateInputs | undefined,
  terminalStageType: string | null,
): Record<string, number> => {
  if (!inputs) return {};
  const pairs =
    FARM_GATE_FIELD_MAP[normalizeStageType(terminalStageType) as StageType];

  if (!pairs) {
    return {};
  }

  return pairs.reduce<Record<string, number>>(
    (acc, { outputField, formField }) => {
      const value = toFiniteNumber(inputs[formField]);
      if (value !== undefined) acc[outputField] = value;
      return acc;
    },
    {},
  );
};

const buildProcessingFarmGateSide = (
  outputs: Record<string, ProcessingOutputItem> | undefined,
): Record<string, number> => {
  if (!outputs) {
    return {};
  }

  return Object.entries(outputs).reduce<Record<string, number>>(
    (acc, [category, item]) => {
      const price = toFiniteNumber(item?.price);
      if (price !== undefined) {
        acc[category] = price;
      }

      return acc;
    },
    {},
  );
};

export const toRoiFormInputs = (
  formState: RoiCalculatorFormData,
  formData: RoiFormData,
): RoiFormInputs => {
  const isProcessing = formData.hasProcessingStage;
  const terminalStageType = formData.terminalOutputStageType;

  const result: RoiFormInputs = {
    currency: formState.currency,
    feedCostPerTonne: buildFeedCostPerTonne(formState.stages ?? []),
    farmGate: {
      baseline: isProcessing
        ? buildProcessingFarmGateSide(formData.processingOutputs?.baseline)
        : buildFarmGateSide(
            formState.farmGate?.baselineInputs,
            terminalStageType,
          ),
      intervention: isProcessing
        ? buildProcessingFarmGateSide(formData.processingOutputs?.intervention)
        : buildFarmGateSide(
            formState.farmGate?.interventionInputs,
            terminalStageType,
          ),
    },
  };

  const inclusionCost = toFiniteNumber(formState.inclusionCostOfSolution);
  if (inclusionCost !== undefined)
    result.inclusionCostOfSolution = inclusionCost;

  const carbonPrice = toFiniteNumber(formState.carbonPricePerTonne);
  if (carbonPrice !== undefined && carbonPrice > 0)
    result.carbonPricePerTonne = carbonPrice;

  return result;
};
